import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Configure axios to always send the token
function getStoredToken() {
  return localStorage.getItem('selah_access_token');
}

function storeToken(token) {
  if (token) {
    localStorage.setItem('selah_access_token', token);
  }
}

function clearToken() {
  localStorage.removeItem('selah_access_token');
}

// Axios interceptor: attach token to every request
axios.interceptors.request.use((config) => {
  config.withCredentials = true;
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Remember me helpers
function getRememberedCredentials() {
  try {
    const saved = localStorage.getItem('selah_remember');
    return saved ? JSON.parse(saved) : null;
  } catch (err) {
    console.debug('[Auth] Could not parse remembered credentials:', err?.message);
    return null;
  }
}

function saveRememberedCredentials(email, password) {
  localStorage.setItem('selah_remember', JSON.stringify({ email, password }));
}

function clearRememberedCredentials() {
  localStorage.removeItem('selah_remember');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch (err) {
      console.debug('[Auth] No active session:', err?.message);
      clearToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      if (data.access_token) {
        storeToken(data.access_token);
      }
      if (rememberMe) {
        saveRememberedCredentials(email, password);
      } else {
        clearRememberedCredentials();
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message 
      };
    }
  };

  // Called after a successful passkey (WebAuthn) login. Same shape as /auth/login.
  const loginWithPasskeyToken = (data) => {
    if (data?.access_token) storeToken(data.access_token);
    setUser(data);
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
      if (data.access_token) {
        storeToken(data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (err) {
      console.debug('[Auth] Logout request failed (already logged out?):', err?.message);
    }
    clearToken();
    setUser(false);
  };

  const updateProfile = async (profileData) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/auth/profile`, profileData);
      setUser(prev => ({ ...prev, ...data }));
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message 
      };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, login, register, logout, updateProfile, checkAuth,
      getRememberedCredentials, loginWithPasskeyToken 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
